import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@fluentui/react-components';
import { Flag16Filled } from '@fluentui/react-icons';
import type { ScheduleEvent } from '@/types/reports';
import { useEventCardStyles } from './EventCard.styles';

export interface EventCardProps {
  event: ScheduleEvent;
  roomColor: string;
  largeEventThreshold: number;
}

const formatTime = (iso: string) => {
  const date = new Date(iso);
  const h = date.getHours();
  const m = date.getMinutes();
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return m === 0 ? `${hour} ${period}` : `${hour}:${m.toString().padStart(2, '0')} ${period}`;
};

export const EventCard = ({ event, roomColor, largeEventThreshold }: EventCardProps) => {
  const styles = useEventCardStyles();
  const { t } = useTranslation('reports');

  const timeRange = useMemo(() => {
    const start = formatTime(event.startTime);
    const end = formatTime(event.endTime);
    return `${start}–${end}`;
  }, [event.startTime, event.endTime]);

  const isLarge = event.guestCount >= largeEventThreshold;

  const tintBg = `color-mix(in srgb, ${roomColor} 12%, transparent)`;

  return (
    <article className={mergeClasses(styles.root, styles.rootResponsive)} data-testid="event-card" aria-label={`${event.title}, ${timeRange}, ${event.roomName}`}>
      <div className={mergeClasses(styles.timeColumn, styles.timeColumnResponsive)} style={{ backgroundColor: tintBg }}>{timeRange}</div>
      <div className={styles.content}>
        <div className={styles.titleRow}>
          <span className={styles.title}>{event.title}</span>
          {isLarge && (
            <span className={styles.largeBadge} data-testid="large-badge" aria-label={t('runOfShow.large')}>
              <Flag16Filled aria-hidden="true" />
              {t('runOfShow.large')}
            </span>
          )}
        </div>
        <div className={styles.metadataRow}>
          <span className={styles.roomName} style={{ color: roomColor }}>{event.roomName}</span>
          <span className={styles.separator}>•</span>
          <span>{event.department}</span>
          <span className={styles.separator}>•</span>
          <span>{event.audienceType}</span>
          <span className={styles.separator}>•</span>
          <span>{event.guestCount} {t('runOfShow.guests')}</span>
          {event.sponsorName && (
            <>
              <span className={styles.separator}>•</span>
              <span>{t('runOfShow.sponsor')}: {event.sponsorName}</span>
            </>
          )}
        </div>
      </div>
      <span className={styles.roomTypeBadge} style={{ color: roomColor }}>
        {event.roomType}
      </span>
    </article>
  );
};

EventCard.displayName = 'EventCard';
